import {
  BlotterTable,
  Box,
  EMPTY_ARRAY,
  isOption,
  LoaderTalos,
  useBlotterTable,
  useConstant,
  useObservableValue,
  type Column,
  type MarketDataTrade,
  type PriceParams,
  type Security,
} from '@talos/kyoko';
import { map } from 'rxjs/operators';
import { useMarketDataTrade } from '../../../../hooks/useMarketDataTrade';
import { BlotterWrapper } from './styles';

interface TradeHistoryParams {
  security: Security;
  selectedMarkets: string[];
}

export const TradeHistory = ({ security, selectedMarkets }: TradeHistoryParams) => {
  const { marketDataTradeObs, errorObservable } = useMarketDataTrade({
    symbol: security.Symbol,
    markets: selectedMarkets.length > 0 ? selectedMarkets : (security.Markets ?? EMPTY_ARRAY),
    tag: 'TradeHistory',
  });

  const columns = useConstant<Column[]>([
    {
      field: 'Price',
      title: `Price ${security.QuoteCurrency}`,
      type: 'price',
      width: 90,
      params: {
        align: 'left',
        asset: security.Symbol,
        showCurrency: false,
        getSide: data => data?.AggressorSide,
      } satisfies PriceParams<MarketDataTrade>,
    },
    { field: 'Market', type: 'market', width: 80, params: { withColor: true } },
    { field: 'Size', title: 'Quantity', type: 'size', width: 70, params: { currency: security.PositionCurrency } },
    {
      field: 'TransactTime',
      title: 'Time',
      type: isOption(security) ? 'date' : 'time',
      sort: '-',
      width: 90,
    },
  ]);

  const loading = useObservableValue(() => marketDataTradeObs.pipe(map(() => false)), [marketDataTradeObs], true);

  const blotterTable = useBlotterTable({
    dataObservable: marketDataTradeObs,
    errorObservable,
    columns,
    rowID: 'TradeID',
    gridOptions: {
      onFirstDataRendered: ({ api }) => {
        api.sizeColumnsToFit();
      },
    },
  });

  return (
    <Box w="100%" h="100%">
      {loading ? (
        <LoaderTalos />
      ) : (
        <BlotterWrapper>
          <BlotterTable {...blotterTable} />
        </BlotterWrapper>
      )}
    </Box>
  );
};
